import { z } from "zod";
import { DEFAULT_MARKETPLACE_POLICY, MarketplaceUploadKindSchema, MarketplaceBreedSchema, MarketplacePricingTypeSchema, MarketplaceSexSchema, } from "./contracts.js";
export { DEFAULT_MARKETPLACE_POLICY };
export const MarketplaceListingFieldsSchema = z
    .object({
    horseName: z.string().trim().min(2).max(100),
    breed: MarketplaceBreedSchema,
    breedOther: z.string().trim().min(2).max(80).nullable().default(null),
    dateOfBirth: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/u)
        .refine(isCalendarDate, "Enter a valid date of birth."),
    sex: MarketplaceSexSchema,
    sire: z.string().trim().min(2).max(100).nullable().default(null),
    dam: z.string().trim().min(2).max(100).nullable().default(null),
    pricingType: MarketplacePricingTypeSchema,
    priceMinor: z
        .string()
        .regex(/^[1-9]\d{0,14}$/)
        .nullable(),
    priceMaxMinor: z
        .string()
        .regex(/^[1-9]\d{0,14}$/)
        .nullable(),
    currency: z
        .string()
        .trim()
        .length(3)
        .transform((value) => value.toUpperCase()),
    countryCode: z
        .string()
        .trim()
        .regex(/^[A-Z]{2}$/u),
    city: z.string().trim().min(2).max(100),
    horseProfileUrl: z.string().trim().url().max(500).nullable().default(null),
    description: z.string().trim().min(1).max(5_000).nullable().default(null),
    vetDisclosureNotes: z
        .string()
        .trim()
        .min(3)
        .max(5_000)
        .nullable()
        .default(null),
})
    .superRefine((value, context) => {
    if (value.breed === "OTHER" && value.breedOther === null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["breedOther"],
            message: "Specify the horse's breed.",
        });
    }
    if (value.breed !== "OTHER" && value.breedOther !== null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["breedOther"],
            message: "A custom breed is only valid when breed is Other.",
        });
    }
    const hasMinimum = value.priceMinor !== null;
    const hasMaximum = value.priceMaxMinor !== null;
    if ((value.pricingType === "EXACT" && (!hasMinimum || hasMaximum)) ||
        (value.pricingType === "RANGE" && (!hasMinimum || !hasMaximum)) ||
        (value.pricingType === "CONTACT" && (hasMinimum || hasMaximum))) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["pricingType"],
            message: "The supplied price does not match the pricing option.",
        });
    }
    if (value.pricingType === "RANGE" &&
        value.priceMinor !== null &&
        value.priceMaxMinor !== null &&
        BigInt(value.priceMinor) >= BigInt(value.priceMaxMinor)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["priceMaxMinor"],
            message: "The maximum price must be greater than the minimum price.",
        });
    }
});
function isCalendarDate(value) {
    const date = new Date(`${value}T00:00:00.000Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().startsWith(value);
}
export const MarketplaceUploadSchema = z
    .object({
    kind: MarketplaceUploadKindSchema,
    filename: z.string().trim().min(1).max(180),
    contentType: z.string().trim().min(1).max(120),
    contentLength: z.number().int().positive(),
})
    .superRefine((value, context) => {
    const allowed = value.kind === "VIDEO"
        ? ["video/mp4", "video/webm"]
        : value.kind === "VET_DOCUMENT"
            ? ["application/pdf", "image/jpeg", "image/png", "image/webp"]
            : ["image/jpeg", "image/png", "image/webp"];
    const maximum = value.kind === "VIDEO"
        ? 100 * 1024 * 1024
        : value.kind === "PHOTO" || value.kind === "SCAN_IMAGE"
            ? 12 * 1024 * 1024
            : 20 * 1024 * 1024;
    if (!allowed.includes(value.contentType)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["contentType"],
            message: "This file type is not supported.",
        });
    }
    const extensions = {
        "image/jpeg": [".jpg", ".jpeg"],
        "image/png": [".png"],
        "image/webp": [".webp"],
        "application/pdf": [".pdf"],
        "video/mp4": [".mp4"],
        "video/webm": [".webm"],
    };
    if (!(extensions[value.contentType]?.some((extension) => value.filename.toLowerCase().endsWith(extension)) ?? false)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["filename"],
            message: "The file extension does not match its content type.",
        });
    }
    if (value.contentLength > maximum) {
        context.addIssue({
            code: z.ZodIssueCode.too_big,
            maximum,
            inclusive: true,
            type: "number",
            path: ["contentLength"],
            message: "This file is too large.",
        });
    }
});
